import { useCallback, useEffect, useState } from "react";
import { ClipboardPaste as ClipboardPasteIcon, ClipboardX, Info } from "lucide-react";
import { useLang } from "@/i18n";

const strings = {
  fr: {
    why: "Pour coller votre adresse en un clic, votre navigateur demande votre autorisation d'accès au presse-papiers. Aucun contenu n'est lu sans action de votre part, ni transmis à un tiers.",
    paste: "Coller le texte",
    manualHint: "Accès refusé ou indisponible : utilisez le bouton « Coller le texte » ou collez manuellement (Ctrl+V).",
    unavailable: "Votre navigateur ne permet pas l'accès au presse-papiers. Collez manuellement (Ctrl+V).",
    readError: "Lecture impossible — collez manuellement (Ctrl+V).",
  },
  en: {
    why: "To paste your address in one click, your browser asks for clipboard access. Nothing is read without an action from you, and nothing is shared with third parties.",
    paste: "Paste text",
    manualHint: "Access denied or unavailable: use the “Paste text” button or paste manually (Ctrl+V).",
    unavailable: "Your browser does not allow clipboard access. Please paste manually (Ctrl+V).",
    readError: "Could not read clipboard — paste manually (Ctrl+V).",
  },
  de: {
    why: "Um Ihre Adresse mit einem Klick einzufügen, benötigt Ihr Browser Ihre Erlaubnis für den Zwischenablage-Zugriff. Es wird nichts ohne Ihre Aktion gelesen und nichts an Dritte übertragen.",
    paste: "Text einfügen",
    manualHint: "Zugriff verweigert oder nicht verfügbar: Nutzen Sie «Text einfügen» oder fügen Sie manuell ein (Ctrl+V).",
    unavailable: "Ihr Browser erlaubt keinen Zwischenablage-Zugriff. Bitte manuell einfügen (Ctrl+V).",
    readError: "Lesen nicht möglich — bitte manuell einfügen (Ctrl+V).",
  },
};

const btnCls =
  "flex shrink-0 items-center gap-2 border border-[#E4DDD0] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C6B64] transition-colors hover:border-[#C87D55] hover:text-[#B36C45]";

/**
 * Manual-only clipboard paste helper.
 * - No automatic reading, no clipboardchange listener, no background access.
 * - A read happens ONLY on an explicit click on "Paste text" (user gesture).
 * - Denied/unavailable never blocks the page: manual paste (Ctrl+V) always works.
 * - Clipboard content is only delivered to the onPaste callback of this page.
 */
export const ClipboardPaste = ({ onPaste }) => {
  const { lang } = useLang();
  const s = strings[lang] || strings.fr;
  // idle | denied | unavailable
  const [state, setState] = useState("idle");
  const [readFailed, setReadFailed] = useState(false);

  const supported =
    typeof window !== "undefined" && window.isSecureContext && !!navigator.clipboard?.readText;

  useEffect(() => {
    if (!supported) setState("unavailable");
  }, [supported]);

  const readOnce = useCallback(async () => {
    setReadFailed(false);
    try {
      const text = await navigator.clipboard.readText();
      if (text) onPaste(text.trim());
    } catch (err) {
      if (err?.name === "NotAllowedError") {
        setState("denied");
      } else {
        setReadFailed(true);
      }
    }
  }, [onPaste]);

  if (state === "unavailable") {
    return (
      <p data-testid="clipboard-status" data-state="unavailable" className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-[#5C6B64]">
        <ClipboardX className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {s.unavailable}
      </p>
    );
  }

  return (
    <div className="mt-3" data-testid="clipboard-status" data-state={state}>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" data-testid="clipboard-paste-button" onClick={readOnce} className={btnCls}>
          <ClipboardPasteIcon className="h-3.5 w-3.5" />
          {s.paste}
        </button>
      </div>
      {state === "idle" && (
        <p data-testid="clipboard-explanation" className="mt-2 flex items-start gap-2 text-xs leading-relaxed text-[#5C6B64]">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#B36C45]" />
          {s.why}
        </p>
      )}
      {state === "denied" && (
        <p data-testid="clipboard-denied-hint" className="mt-2 flex items-start gap-2 text-xs leading-relaxed text-[#5C6B64]">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#B36C45]" />
          {s.manualHint}
        </p>
      )}
      {readFailed && state !== "denied" && (
        <p data-testid="clipboard-read-error" className="mt-2 text-xs leading-relaxed text-[#5C6B64]">
          {s.readError}
        </p>
      )}
    </div>
  );
};
